import type { Skill, Species } from '../../types';
import type { View, ViewContext } from '../router';
import { birdleStanding, TURNS } from '../../core/birdle';
import { el, ensureStyle } from '../dom';
import { icon, type IconName } from '../components/icons';
import { donutRing } from '../components/donut-ring';
import {
  ALL_PACKS,
  getSpecies,
  newestBatch,
  newestBatchDays,
  unionSpeciesCount,
  type PackSummary,
} from '../../core/region';
import { studyCodes, studyList } from '../../core/ignored';
import { currentSelection, loadSelectedPacks, selectPacks } from '../../core/packs';
import { photosReady } from '../../core/media/image';
import { openModeMenu } from '../components/mode-menu';
import { type Focus } from '../../core/quiz/focus';
import {
  dayKey,
  FAILED_DAYS,
  reviewHistory,
  streakStatus,
  HARD_SOUND_MASTERY,
  masteryBySkill,
  mostFailed,
  soundHardUnlocked,
  speciesStandings,
  type DayTally,
  type StreakStatus,
  weakestSpecies,
  WEAKEST_MIN_REPS,
  type FailedSpecies,
  type MasteryReport,
  type WeakestSpecies,
} from '../../core/progress';
import {
  dueQueue,
  REVIEW_FLOOR,
  SECONDS_PER_QUESTION,
  type DueQueue,
} from '../../core/quiz/field';
import { drillPairs, type DrillPair } from '../../core/quiz/pairs';
import { WITHOUT_CALLS } from '../../core/quiz/sound';
import { soundWithCalls } from '../../core/quiz/settings';
import { t } from '../../strings';
import { listOf, readableDay, weekdayInitial } from '../../strings/format';

ensureStyle(
  'home-css',
  `
  .home { padding:15px var(--pad) 8px; display:flex; flex-direction:column; gap:17px; }
  .home header { display:flex; justify-content:space-between; align-items:flex-start;
    flex-wrap:wrap; gap:10px 14px; }
  .home h1 { font-size:40px; margin:0; line-height:.95; }
  .home .subtitle { margin-top:7px; display:block; }
  /* The pack picker wears the subtitle it replaced. It sat here as dead text
     naming the one region there was; now it is the control that chooses among
     them, and it should still read as a subtitle rather than as a form field —
     the chevron and the pointer are the only things that say it is tappable. */
  .home .packpick { margin-top:5px; display:block; appearance:none; border:0;
    background:transparent; padding:2px 17px 2px 0; margin-left:-1px;
    color:var(--ink-soft); cursor:pointer; max-width:min(62vw, 320px);
    font-family:var(--font-display); font-weight:560; font-size:9.4px;
    letter-spacing:.12em; text-transform:uppercase;
    background-repeat:no-repeat; background-position:right 2px center;
    background-size:9px;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23888' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E"); }
  .home .packpick:focus-visible { outline:2px solid var(--accent); outline-offset:3px; }
  /* The options themselves are drawn by the OS, which does not inherit any of
     the above — give them readable colours in both themes at least. */
  .home .packpick option { font-family:var(--font-body); font-size:14px;
    letter-spacing:normal; text-transform:none;
    color:var(--ink); background:var(--paper); }
  /* ---- The week strip -----------------------------------------------------
     Seven days, one dot each, and what they add up to. It replaced an ember and
     a number in a pill: the number was a thing to lose and said nothing about
     the week, where the dots say which days, and that the pale one cost nothing
     because the week's rest day covered it.

     Beside the title rather than under it: the header row is empty to the right
     of "Syrinx", and a week is a title-sized fact. Right-aligned, so the dots
     and the caption both end on the page's right margin instead of floating in
     the middle of the row. */
  /* Stacked, the way the week reads: the dots, the days they are, and then the
     line that adds them up — the caption is a caption, not a second column.
     Only as wide as its content, so the tap target is the strip itself rather
     than an invisible full-width band.

     On a screen too narrow to hold the caption beside the title the header
     wraps; margin-left:auto keeps the strip on the right of its own row rather
     than dropping it back to the left margin. */
  .home .streak { display:flex; flex-direction:column; align-items:flex-end;
    gap:5px; margin-left:auto; margin-top:15px; max-width:100%; padding:0;
    background:none; border:0; color:var(--ink); cursor:pointer; text-align:right; }
  .streak .dots { display:flex; gap:5px; }
  .streak .day { display:flex; flex-direction:column; align-items:center; gap:3px; }
  .streak .d { width:12px; height:12px; border-radius:999px;
    background:var(--sunk); border:1.5px solid var(--hairline-strong); }
  /* Studied. The moss green the app uses for "done" everywhere else. */
  .streak .d.on { background:var(--moss); border-color:transparent; }
  /* Today is a ring in the accent until it is answered, and fills with it after
     — the one dot whose state the learner can still change. */
  .streak .d.today { border-color:var(--accent); border-width:2px; }
  .streak .d.today.on { background:var(--accent); }
  .streak .k { font-family:var(--font-display); font-weight:560; font-size:7.6px;
    letter-spacing:.1em; line-height:1; color:var(--ink-soft); }
  .streak .cap { font-size:10.8px; line-height:1.2; white-space:nowrap; }
  /* A live streak with nothing answered today: the caption stops reporting and
     starts asking. Until this, it read the same at nine in the morning as at
     midnight, which is the one hour it had anything to say. */
  .streak.atrisk .cap { color:var(--danger); }
  .ringblock { display:flex; flex-direction:column; gap:7px; }
  .masteryhead { display:flex; align-items:baseline; justify-content:space-between; gap:10px; }
  /* Small-caps like the section label beside it, underlined so it reads as the
     way out to an explanation rather than as more caption. */
  .masteryhead .what { padding:0; font-family:var(--font-display); font-weight:560;
    font-size:9.4px; letter-spacing:.12em; text-transform:uppercase;
    color:var(--ink-soft); text-decoration:underline; text-underline-offset:3px; }
  /* The rings measure the birds you've met; this line is where the rest of the
     region is accounted for, so adding species reads as "more to meet" rather
     than as a ring quietly losing points. */
  .coverage { display:flex; align-items:center; justify-content:center; gap:7px;
    text-align:center; }
  .coverage .bar { flex:1; max-width:120px; }
  /* Sunk rather than raised: a footnote to the rings above it, not a fourth
     card. The name carries the accent, because the name is the link. */
  .weakest { display:flex; align-items:center; justify-content:space-between;
    gap:12px; width:100%; padding:9px 13px; text-align:left; cursor:pointer;
    background:var(--sunk); border:1.5px solid var(--hairline);
    border-radius:var(--radius-hand-alt); color:var(--ink); }
  .weakest .nm { font-family:var(--font-display); font-weight:560; font-size:14px;
    color:var(--accent); text-align:right; }
  .section-label { display:block; margin-bottom:9px; }
  /* ---- Due today ---------------------------------------------------------
     A card, not a skill-coloured panel: the round it opens belongs to all three
     skills at once, and filling it with one of the three hues would say it was
     mostly that one. The tiles carry the hues instead, a stripe each. */
  .duepanel { display:flex; flex-direction:column; gap:11px; padding:14px 15px 15px; }
  .duepanel .top { display:flex; justify-content:space-between; align-items:baseline; gap:8px; }
  .duepanel .est { font-variant-numeric:tabular-nums; }
  .duepanel h2 { font-family:var(--font-display); font-size:21px; margin:0;
    line-height:1.2; }
  .duegrid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  /* Sunk wells rather than raised cards: they are readouts, not three more
     things to tap — the one control on the panel is the button under them. */
  .duetile { display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:9px 4px 8px; background:var(--sunk); border:1.5px solid var(--hairline);
    border-radius:var(--radius); border-top:3px solid var(--sk); }
  .duetile .n { font-family:var(--font-display); font-weight:600; font-size:25px;
    line-height:1.05; font-variant-numeric:tabular-nums; }
  .duetile .label { font-size:8.6px; }
  .duepanel .clear { width:100%; }
  /* Why the button's number is smaller than the tiles' sum. */
  .duepanel .note-capped { text-align:center; margin-top:-3px; }
  .studygrid { display:grid; grid-template-columns:1fr 1fr; gap:9px; }
  /* Smaller than they were: four cards used to run past the fold on a phone
     once the panel above them grew. The icon sits ON the title rather
     than above it — pinned to its own row it read as decoration, and the card
     had to be a row taller to hold it. */
  .studycard { padding:11px 12px 10px; min-height:74px;
    display:flex; flex-direction:column; justify-content:flex-end; gap:3px;
    text-decoration:none; }
  /* The pool cards are buttons rather than links, because what they open is a
     menu and not a destination — so they need the resets a button does not
     inherit. Everything else about them is the card above. */
  button.studycard { width:100%; text-align:left; font:inherit; }
  .studycard .head { display:flex; align-items:center; gap:7px; }
  .studycard .head .icon { opacity:.85; }
  /* The title takes the slack, which is what puts the badge and the options
     button at the right-hand end of the row whether or not the other is there.
     (It used to be the badge's own margin-left:auto, which stopped working the
     moment a second thing wanted the same corner.) */
  .studycard .title { font-family:var(--font-display); font-size:17px; line-height:1.1;
    margin-right:auto; }
  .studycard .sub { font-size:10.5px; opacity:.8; line-height:1.3; }
  /* Hard mode's only announcement. Hairline in currentColor, so it reads on the
     filled cards and the outline one without a per-variant rule. */
  .studycard .badge { flex:none; color:inherit;
    font-size:8.2px; letter-spacing:.1em; line-height:1;
    border:1.5px solid currentColor; border-radius:999px; padding:3px 6px; opacity:.85; }
  /* The way back to the setup screen, now that the card itself starts the round.
     Quiet and small: it is the rarer of the two things a card can do, and the
     card is the other one. Its own tap target is padded out past the glyph so a
     thumb aiming for it doesn't start a round instead. */
  .studycard .opts { flex:none; margin:-8px -6px -8px 0; padding:8px 6px;
    color:inherit; opacity:.7; background:none; border:none; line-height:0; }
  .studycard .opts:hover, .studycard .opts:focus-visible { opacity:1; }
  /* Each live mode carries its mastery-ring colour, so the grid, the rings and
     the Stats bars all agree on what "Name Match" is coloured. */
  .studycard.filled { background:var(--skill-sound); color:var(--on-dark);
    border-color:transparent; }
  .studycard.filled.alt { background:var(--skill-names); }
  .studycard.filled.photo { background:var(--skill-photo); }
  .studycard.outline { color:var(--ink); }
  .studycard.outline .sub { color:var(--ink-soft); }
  /* Two cards span the grid rather than taking a fifth tile, because neither is
     a fourth skill: the mix-up drill at the top, a round built out of the other
     three going wrong and only here at all on the days the review log has pairs
     to offer, and the Library at the foot, which is not a round at all. */
  .studycard.wide { grid-column:1 / -1; min-height:0; }
  .studycard.wide .sub { opacity:1; }
  /* The Field Test spans the grid for the same reason and wears all three skill
     colours for it: it is not a fourth skill either, it is the three of them
     dealt in one round. The gradient is the only place the app puts the hues
     together, and it is the whole claim the card is making. It keeps a tile's
     height rather than a wide card's row, because unlike the mix-up offer it is
     always there and it is the headline of the section. */
  .studycard.mixed { grid-column:1 / -1; min-height:64px; color:var(--on-dark);
    border-color:transparent;
    background:linear-gradient(103deg, var(--skill-sound) 0%,
      var(--skill-names) 52%, var(--skill-photo) 100%); }
  .studycard.mixed .sub { opacity:.92; }
  /* Birdle spans the grid, in the accent rather than a skill hue, because it is
     not a skill and never will be: nothing it asks is Sound ID, Name Match or
     Photo ID, and nothing it answers moves a card. It sits at the foot with the
     Library, which is the other thing down there that is not a round. */
  .studycard.daily { grid-column:1 / -1; min-height:0; background:var(--accent);
    color:var(--on-accent); border-color:transparent; }
  .studycard.daily .sub { opacity:.92; }
  /* Birdle's row: the card, and beside it the way to its calendar. A second
     tile rather than a control inside the card, as the round cards' options
     are, because it goes somewhere of its own — the days already played and the
     ones that were missed — and is as likely to be the reason for the visit as
     today's bird is. The row takes the grid's place in the grid, gap and all. */
  .dailyrow { grid-column:1 / -1; display:flex; gap:inherit; }
  .dailyrow .studycard.daily { flex:1; min-width:0; }
  .dailyrow .studycard.cal { flex:none; width:58px; min-height:0; padding:0;
    align-items:center; justify-content:center; background:var(--accent);
    color:var(--on-accent); border-color:transparent; }
  /* ---- Phones -------------------------------------------------------------
     The screen holds five blocks — header, rings, coverage, due panel, study
     grid — and on a narrow or short phone the gaps between them were spending
     more height than several of the blocks themselves. Everything here is
     vertical air; not one type size or tap target changes, so the screen gets
     tighter without getting smaller to use. */
  @media (max-width:430px), (max-height:740px) {
    .home { padding:11px var(--pad) 6px; gap:13px; }
    .home h1 { font-size:36px; }
    .home .ringblock { gap:6px; }
    /* Scoped to Home: the Stats screen shows the same three rings inside a card
       that has room for them. */
    .home .ring { gap:5px; }
    .home .weakest { padding:8px 12px; }
    .home .duepanel { gap:9px; padding:12px 13px 13px; }
    .home .duetile { padding:7px 4px 6px; }
    .home .section-label { margin-bottom:7px; }
    .home .studygrid { gap:8px; }
    .home .studycard { padding:9px 11px 8px; min-height:66px; }
    .home .studycard.mixed { min-height:58px; }
  }
`,
);

/** Each skill's name, wherever the home screen writes one out. */
const MODE_LABEL: Record<Skill, string> = {
  sound: t('common.skill.sound'),
  names: t('home.modeLabel.names'),
  photo: t('common.skill.photo'),
};

/** Where each skill's own round lives. */
const MODE_PATH: Record<Skill, string> = {
  sound: '/quiz/sound',
  names: '/quiz/names',
  photo: '/quiz/photo',
};

/** The mastery rings, left to right, each in its skill's colour. */
const RINGS: { skill: Skill; caption: string; color: string }[] = [
  { skill: 'sound', caption: t('common.skillRing.sound'), color: 'var(--skill-sound)' },
  { skill: 'names', caption: t('common.skillRing.names'), color: 'var(--skill-names)' },
  { skill: 'photo', caption: t('common.skillRing.photo'), color: 'var(--skill-photo)' },
];

export const homeView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const [packs, selection] = await Promise.all([loadSelectedPacks(), currentSelection()]);
  const [mastery, streak, weakest, week] = await Promise.all([
    masteryBySkill(studyCodes()),
    streakStatus(),
    weakestSpecies(studyCodes()),
    // The trailing seven days, oldest first — one dot each.
    reviewHistory(WEEK, new Date()),
  ]);
  // Sound ID changes shape once the learner has done enough of it (see
  // soundHardUnlocked), and can be switched either way on its setup screen; the
  // card reads the setting rather than the unlock, so it says what the next
  // round will actually be.
  //
  // Read before the due queue and the mix-up drill rather than alongside them,
  // because both of them deal sound questions and both have to be built from the
  // voices this learner has actually asked for.
  const hardSound = await soundWithCalls(soundHardUnlocked(mastery));
  const voices = hardSound ? undefined : WITHOUT_CALLS;
  // Photo cards can be due while the media pack is not downloaded, and a photo
  // question without the pack is a placeholder. Counting them would promise a
  // round the app can't deal, so the skill drops out of both the count and the
  // round — exactly as it does in the Field Test.
  const skills: Skill[] = (await photosReady())
    ? ['sound', 'names', 'photo']
    : ['sound', 'names'];
  // The daily puzzle's state, for the card's badge. Nothing is fetched: the
  // manifest is in memory and so is the settings map, so this is a hash over the
  // pool and no I/O.
  const birdle = await birdleStanding();
  // The newest birds in each pack, and how many of them are still unmet. Both
  // read the study list rather than the region, so a batch of twenty with three
  // set aside is a card that offers seventeen.
  const batch = newestBatch(studyList());
  const [due, pairs, failed, batchStandings] = await Promise.all([
    dueQueue(new Date(), { skills, voices }),
    // Pairs the learner has actually mixed up, drawn from the review log,
    // narrowed to the ones a round can actually be built from.
    drillPairs(new Date(), voices),
    // Birds a wrong answer keeps landing on. Unscoped by design (see
    // `mostFailed`), so narrow it to what is actually loaded before offering a
    // round built from it.
    mostFailed(),
    speciesStandings(batch.map((sp) => sp.code)),
  ]);
  const studyable = new Set(studyCodes());
  const failedHere = failed.filter((f) => studyable.has(f.code));
  // The batch card retires itself. It exists because the newest birds are the
  // last thing the schedule reaches — added commonest-first, so they sit at the
  // back of the introduction order — and once they have all been met that is no
  // longer true of them and the ordinary rounds have them covered.
  const batchUnmet = batch.filter((sp) => batchStandings.get(sp.code)?.neverMet).length;

  const header = el(
    'header',
    {},
    el('div', {}, el('h1', {}, t('app.meta.name')), packPicker(packs, selection, ctx)),
    streakStrip(streak, week, ctx),
  );

  const rings = el(
    'div.ringblock',
    {},
    el(
      'div.masteryhead',
      {},
      el('span.label', {}, t('common.mastery.label')),
      el(
        'button.what.btn.btn-plain',
        { onclick: () => ctx.navigate('/stats'), title: t('home.mastery.whatTitle') },
        t('home.mastery.what'),
      ),
    ),
    el(
      'div.ringrow',
      {},
      ...RINGS.map(({ skill, caption, color }) => {
        const m = mastery.bySkill[skill];
        return donutRing({
          percent: m.percent,
          caption,
          color,
          // The fraction the ring's percentage is actually taken from — solid
          // out of MET, not out of the region. The birds still to meet are the
          // coverage line's business, one row down.
          detail: t('common.mastery.ringDetail', { mastered: m.mastered, started: m.started }),
          label: t('common.mastery.ringLabel', {
            skill: caption,
            percent: m.percent,
            started: m.started,
          }),
        });
      }),
    ),
    coverageLine(mastery),
    weakestLine(weakest, ctx),
  );

  const study = el(
    'section',
    {},
    el('div.section-label.label', {}, t('home.study.label')),
    el(
      'div.studygrid',
      {},
      pairs.length ? mixUpCard(pairs, ctx) : null,
      // Beside the mix-up drill because it is the same kind of offer built from
      // the same evidence — the review log rather than the schedule — only per
      // bird instead of per pair.
      failedHere.length ? mostFailedCard(failedHere, ctx) : null,
      // All three skills in one round, dealt in an order the learner can't
      // pre-load for — the closest thing to being out with binoculars the app
      // can ask for, and the reason it heads the grid rather than sitting in it.
      roundCard(
        'binoculars',
        t('common.mode.field'),
        t('home.field.sub'),
        'mixed',
        '/quiz/field',
        ctx,
        {
          badge: t('home.field.badge'),
          title: t('home.field.title'),
        },
      ),
      roundCard(
        'music',
        t('common.mode.sound'),
        hardSound ? t('home.sound.subHard') : t('home.sound.sub'),
        'filled',
        '/quiz/sound',
        ctx,
        hardSound
          ? {
              badge: t('home.sound.badgeHard'),
              title: t('home.sound.titleHard', { percent: HARD_SOUND_MASTERY }),
            }
          : undefined,
      ),
      roundCard(
        'tag',
        t('common.mode.names'),
        t('home.names.sub'),
        'filled alt',
        '/quiz/names',
        ctx,
      ),
      roundCard(
        'eye',
        t('common.mode.photo'),
        t('home.photo.sub'),
        'filled photo',
        '/quiz/photo',
        ctx,
      ),
      // Sound ID asked from the other side: the bird is shown, two recordings
      // are offered, and the wrong one is the bird this one is most often taken
      // for. It takes the fourth tile because it is a round to run like the
      // three beside it, and it fills the cell they would otherwise leave odd.
      roundCard(
        'waveform',
        t('common.mode.soundMatch'),
        t('home.soundMatch.sub'),
        'filled',
        '/quiz/sound-match',
        ctx,
        {
          badge: t('home.soundMatch.badge'),
          title: t('home.soundMatch.title'),
        },
      ),
      // One bird a day, the same bird for everyone on the same packs, asked
      // about by what it eats and where it lives rather than by its voice. The
      // badge is the day's own state, because a daily you have already played is
      // a different offer from one you haven't.
      el(
        'div.dailyrow',
        {},
        studyCard(
          'birdle',
          t('home.birdle.title'),
          birdle.over ? t('home.birdle.subDone') : t('home.birdle.sub'),
          'daily',
          '/birdle',
          ctx,
          {
            badge: !birdle.ready
              ? t('home.birdle.badgeSoon')
              : birdle.over
                ? birdle.won
                  ? t('home.birdle.badgeWon', { turns: birdle.turns, max: TURNS })
                  : t('home.birdle.badgeLost', { max: TURNS })
                : birdle.turns
                  ? t('home.birdle.badgeTurn', { turn: birdle.turns + 1 })
                  : t('home.birdle.badgeToday'),
            title: t('home.birdle.cardTitle'),
          },
        ),
        calendarCard(ctx),
      ),
      // Above the Library rather than up with the mix-up drill, because like
      // the Library it is a fact about the pack rather than about the learner —
      // and because three wide cards stacked over the grid would push the modes
      // themselves off a phone screen.
      batchUnmet ? justAddedCard(batch, batchUnmet, ctx) : null,
      // The foot of the grid, and of the screen. It spans rather than sitting
      // among the tiles because it is not a round at all: nothing is asked and
      // nothing is scored, it is the shelf the rounds are drawn from.
      studyCard(
        'book',
        t('common.nav.library'),
        t('home.library.sub', { n: mastery.total }),
        'outline wide',
        '/library',
        ctx,
      ),
    ),
  );

  root.append(
    el(
      'div.home',
      {},
      header,
      rings,
      reviewPanel(due, skills, ctx),
      study,
    ),
  );
};

/**
 * The pack picker: every pack the app ships, plus "All packs".
 *
 * Choosing one installs it and re-renders. No reload — every view reads the
 * species universe inside its own factory, so Library, Stats and the quizzes
 * pick up the new set the next time they render, and the rings on this screen
 * rescale on the spot, which is the confirmation that the switch took.
 *
 * "All packs" is offered only when there is more than one to combine, and its
 * count is the deduped union rather than the sum: a bird in two packs is one
 * bird, one FSRS card, and one entry in the rotation.
 */
function packPicker(
  packs: PackSummary[],
  selection: string,
  ctx: ViewContext,
): HTMLElement {
  const select = el('select.packpick', {
    'aria-label': t('home.packs.pickerLabel'),
  }) as HTMLSelectElement;
  for (const p of packs) {
    select.append(
      el('option', { value: p.id }, t('home.packs.option', { pack: p.label, count: p.speciesCount })),
    );
  }
  if (packs.length > 1) {
    // The deduped count, straight from the index — a bird in two packs is one
    // bird here, so this is deliberately not the sum of the options above.
    select.append(
      el('option', { value: ALL_PACKS }, t('home.packs.all', { count: unionSpeciesCount() })),
    );
  }
  select.value = selection;
  select.addEventListener('change', () => {
    void (async () => {
      select.disabled = true;
      await selectPacks(select.value);
      ctx.navigate('/home');
    })();
  });
  return select;
}

/** Days in the strip. A week, so the rest-day allowance and the row agree. */
const WEEK = 7;

/**
 * The last seven days, one dot each, and what the run adds up to.
 *
 * It replaced a pill with a number in it. A number is a thing to lose: it says
 * "14" and then one day says "0", and the only information it ever carried was
 * whether you had already studied today — which is the one thing the learner
 * already knows. Seven dots say something a number can't: WHICH days, how the
 * week has actually gone, and that Wednesday cost you nothing because the week's
 * rest day covered it.
 *
 * Trailing seven days rather than Monday-to-Sunday, so the row is always about
 * the week you are in the middle of rather than resetting to empty every Monday
 * morning. The rest-day allowance underneath it is per calendar week — see
 * WEEKLY_REST_DAYS — because a rolling allowance is not a rule anyone can hold
 * in their head.
 */
function streakStrip(
  streak: StreakStatus,
  week: DayTally[],
  ctx: ViewContext,
): HTMLElement {
  const today = dayKey(new Date());
  const dots = el('div.dots', {});
  for (const day of week) {
    const studied = day.total > 0;
    const isToday = day.day === today;
    const dot = el('span.d', {
      title: studied
        ? t('home.streak.dayAnswered', { day: day.day, n: day.total })
        : t('home.streak.dayNothing', { day: day.day }),
    });
    if (studied) dot.classList.add('on');
    if (isToday) dot.classList.add('today');
    dots.append(
      el('span.day', {}, dot, el('span.k', {}, weekdayInitial(day.day))),
    );
  }

  const rest = streak.restDaysLeft;
  const caption = streak.days
    ? t('home.streak.running', { days: streak.days, rest })
    : streak.studiedToday
      ? t('home.streak.startsToday')
      : t('home.streak.none');

  const strip = el(
    'button.streak',
    {
      onclick: () => ctx.navigate('/stats'),
      title: streak.atRisk
        ? t('home.streak.atRiskTitle', { days: streak.days })
        : t('home.streak.weekTitle'),
    },
    dots,
    el('span.cap.meta', {}, caption),
  );
  if (streak.atRisk) strip.classList.add('atrisk');
  return strip;
}

/**
 * "31 of 120 birds met" — the coverage the rings deliberately no longer carry.
 * Reads as a goal rather than a deficit, and is the one place a species being
 * added to the region is supposed to change a number.
 */
function coverageLine(m: MasteryReport): HTMLElement {
  const pct = m.total ? Math.min(100, Math.round((m.speciesStarted / m.total) * 100)) : 0;
  return el(
    'div.coverage.meta',
    {},
    t('home.coverage.met', { met: m.speciesStarted, total: m.total }),
    el('div.bar.meter', {}, el('i', { style: { width: `${pct}%` } })),
    m.speciesStarted >= m.total && m.total > 0
      ? t('home.coverage.allMet')
      : t('home.coverage.toGo', { n: m.total - m.speciesStarted }),
  );
}

/**
 * The bird to worry about, under the three rings.
 *
 * The rings say which SKILL is lagging, which is a fact you can't act on
 * directly — nobody studies "photos", they study some particular bird. This
 * names the one the scheduler is least confident about across all three skills,
 * and taps through to it.
 *
 * Absent rather than empty when nothing qualifies: before any bird has been
 * answered WEAKEST_MIN_REPS times there is no weakest bird, only a newest one,
 * and a row saying so would be noise on the screen a new learner sees most.
 */
function weakestLine(
  weak: WeakestSpecies | null,
  ctx: ViewContext,
): HTMLElement | null {
  const sp = weak && getSpecies(weak.code);
  if (!weak || !sp) return null;
  return el(
    'button.weakest',
    {
      onclick: () => ctx.navigate(`/species/${weak.code}`),
      title: t('home.weakest.title', {
        percent: Math.round(weak.retrievability * 100),
        reps: weak.reps,
        min: WEAKEST_MIN_REPS,
      }),
    },
    el('span.label', {}, t('home.weakest.label')),
    el('span.nm', {}, sp.common),
  );
}

/**
 * "Due today": what the scheduler has asked for back, and the one button that
 * clears it.
 *
 * This is where the daily challenge used to be. A challenge was one question a
 * day off a fixed three-skill rotation — a streak-keeper that had nothing to do
 * with what was actually due, so on a day with forty cards waiting it asked one
 * bird and called the day done. The panel now reports the deck: every card that
 * has come around among the birds this learner has met, split by skill, and a
 * round that takes the most overdue of them mixed.
 *
 * The three counts are the WHOLE queue; the round is capped (see REVIEW_CAP) and
 * the minutes are the round's, not the queue's. A learner three weeks behind
 * should see three weeks of work and a round they can finish, not one number
 * that quietly means both.
 *
 * Gone entirely below REVIEW_FLOOR rather than sitting there small. It used to
 * hold an "All caught up" card at zero and offer to clear a queue of one, so on
 * most visits the biggest block on the screen was announcing that it had no
 * work — and a round, a scorecard and a walk back to Home is a lot of ceremony
 * for two questions. A queue that small keeps; the panel is back the moment it
 * is worth a sitting.
 */
function reviewPanel(
  due: DueQueue,
  skills: Skill[],
  ctx: ViewContext,
): HTMLElement | null {
  if (due.total < REVIEW_FLOOR) return null;
  const panel = el(
    'section.duepanel.card',
    {},
    el(
      'div.top',
      {},
      el('span.label', {}, t('home.due.label')),
      el(
        'span.est.meta',
        { title: t('home.due.estimateTitle', { seconds: SECONDS_PER_QUESTION }) },
        t('home.due.estimate', { minutes: due.minutes }),
      ),
    ),
  );

  panel.append(
    el(
      'div.duegrid',
      {},
      ...RINGS.filter((r) => skills.includes(r.skill)).map(({ skill, caption }) =>
        el(
          `div.duetile.sk-${skill}`,
          { title: t(`home.due.tileTitle.${skill}`, { n: due.bySkill[skill] }) },
          el('span.n', {}, String(due.bySkill[skill])),
          el('span.label', {}, caption),
        ),
      ),
    ),
    el(
      'button.clear.btn.btn-primary',
      { onclick: () => ctx.navigate('/quiz/field?mode=review') },
      due.capped ? t('home.due.clearSome', { n: due.round }) : t('home.due.clearAll'),
      icon('arrow-right', { size: 18 }),
    ),
  );
  if (due.capped) {
    panel.append(
      // Why the button's number is smaller than the tiles add up to. The round
      // itself says the rest of it — "Clear 20 of them".
      el('div.meta.note-capped', {}, t('home.due.cappedNote', { n: due.total })),
    );
  }
  return panel;
}

/**
 * The drill offer: how many pairs are outstanding, and the one at the top.
 *
 * Named rather than counted, because "3 pairs" is a statistic and "Pacific
 * Wren ↔ Winter Wren" is the reason to tap. The skill is the top pair's, and
 * the drill runs in that skill alone — mixing a sound pair and a photo pair
 * into one round would be two exercises wearing one name.
 */
function mixUpCard(pairs: DrillPair[], ctx: ViewContext): HTMLElement {
  const top = pairs[0];
  const same = pairs.filter((p) => p.skill === top.skill).length;
  const a = getSpecies(top.a)?.common ?? top.a;
  const b = getSpecies(top.b)?.common ?? top.b;
  return studyCard(
    'swap',
    t('home.mixUps.title'),
    t('home.mixUps.sub', { mode: MODE_LABEL[top.skill], a, b }),
    'outline wide',
    `${MODE_PATH[top.skill]}?drill=pairs`,
    ctx,
    {
      badge: t('home.mixUps.badge', { n: same }),
      title: t('home.mixUps.cardTitle', { mode: MODE_LABEL[top.skill] }),
    },
  );
}

/**
 * The birds that keep going wrong, as a round.
 *
 * Named for the record rather than for the model — see `mostFailed`, and the
 * weakest-bird line up under the rings, which answers the neighbouring question
 * and often names a different bird.
 */
function mostFailedCard(failed: FailedSpecies[], ctx: ViewContext): HTMLElement {
  const top = failed[0];
  const name = getSpecies(top.code)?.common ?? top.code;
  const codes = failed.map((f) => f.code);
  return poolCard(
    'alert',
    t('home.mostFailed.title'),
    t('home.mostFailed.sub', { name, wrong: top.wrong, asked: top.asked }),
    { kind: 'failed', codes },
    codes,
    ctx,
    {
      badge: t('common.birds', { n: codes.length }),
      title: t('home.mostFailed.cardTitle', { days: FAILED_DAYS }),
    },
  );
}

/**
 * The newest birds in the pack, as a round.
 *
 * Labelled by what it is — this many birds, added on this day — rather than as
 * a recommendation, because it is one. Batches are added commonest-first, so
 * these are by construction the least common birds in the pack, and pushing
 * them at someone still learning the robins is exactly what `introductionRank`
 * exists to prevent. The reason to offer them at all is that the schedule
 * introduces unmet species in rank order a fraction of a round at a time, which
 * puts a new batch dozens of rounds away and further with every batch after it.
 */
function justAddedCard(batch: Species[], unmet: number, ctx: ViewContext): HTMLElement {
  const codes = batch.map((sp) => sp.code);
  const days = [...newestBatchDays(studyList()).values()].sort().map(readableDay);
  return poolCard(
    'feather',
    t('home.justAdded.title'),
    t('home.justAdded.sub', { days: listOf(days), n: unmet }),
    { kind: 'batch', codes },
    codes,
    ctx,
    {
      badge: t('common.birds', { n: codes.length }),
      title: t('home.justAdded.cardTitle'),
    },
  );
}

/**
 * A card that offers a pool of birds rather than a mode.
 *
 * The two cards built this way — the birds that keep going wrong, and the ones
 * just added to the pack — name a set of species and say nothing about which
 * sense to study them by, exactly as a filtered Library list does. So they use
 * the same menu the Library's Study button uses, rather than each guessing a
 * mode: an evening on the newest twenty might be by ear or by eye, and the card
 * has no business deciding.
 */
function poolCard(
  ico: IconName,
  title: string,
  sub: string,
  focus: Focus,
  codes: string[],
  ctx: ViewContext,
  mark: { badge: string; title: string },
): HTMLElement {
  const card = cardBody('button.studycard.card', ico, title, sub, 'outline wide', mark);
  card.setAttribute('type', 'button');
  card.addEventListener('click', () =>
    openModeMenu(card, { focus, codes, navigate: (p) => ctx.navigate(p) }),
  );
  return card;
}

function studyCard(
  ico: IconName,
  title: string,
  sub: string,
  variant: string,
  path: string,
  ctx: ViewContext,
  mark?: { badge: string; title: string },
): HTMLElement {
  const card = cardBody('a.studycard.card', ico, title, sub, variant, mark);
  card.setAttribute('href', `#${path}`);
  card.addEventListener('click', (e) => {
    e.preventDefault();
    ctx.navigate(path);
  });
  return card;
}

/**
 * A mode's card: one tap and the round is running.
 *
 * It used to land on the mode's setup screen, every time, which meant the
 * commonest thing anyone does in this app — "another ten, the way I had it" —
 * cost a screen of options they had already answered and would answer the same
 * way again. Every mode remembers what it was last run with (see
 * core/quiz/settings.ts), so `?go=1` deals the round with those and opens on
 * the first question.
 *
 * The options are still one tap away, on the small control in the card's own
 * head — and again on "Change options" at the end of every round, which is
 * where wanting them usually happens.
 *
 * Only the five mode cards. The Library and Birdle have no round to start, and
 * the mix-up drill keeps its setup screen because that screen is the only place
 * that says what a drill IS.
 */
function roundCard(
  ico: IconName,
  title: string,
  sub: string,
  variant: string,
  path: string,
  ctx: ViewContext,
  mark?: { badge: string; title: string },
): HTMLElement {
  const card = studyCard(ico, title, sub, variant, `${path}?go=1`, ctx, mark);
  const opts = el(
    'button.opts',
    {
      type: 'button',
      title: t('home.study.optionsTitle', { mode: title }),
      'aria-label': t('home.study.optionsLabel', { mode: title }),
    },
    icon('sliders', { size: 16 }),
  );
  // Inside the card, but not the card's own tap: without both of these, pressing
  // it would set a round going behind the setup screen it asked for.
  opts.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    ctx.navigate(path);
  });
  card.querySelector('.head')?.append(opts);
  return card;
}

/** The tile beside Birdle: its calendar. An icon alone, so it is named for a screen reader. */
function calendarCard(ctx: ViewContext): HTMLElement {
  const path = '/birdle/calendar';
  const label = t('home.birdle.calendar');
  const card = el(
    'a.studycard.card.cal',
    { href: `#${path}`, title: label, 'aria-label': label },
    icon('calendar', { size: 22 }),
  );
  card.addEventListener('click', (e) => {
    e.preventDefault();
    ctx.navigate(path);
  });
  return card;
}

/** The markup both kinds share; only the element and the click differ. */
function cardBody(
  sel: string,
  ico: IconName,
  title: string,
  sub: string,
  variant: string,
  mark?: { badge: string; title: string },
): HTMLElement {
  const card = el(
    sel,
    { title: mark?.title ?? null },
    el(
      'div.head',
      {},
      icon(ico, { size: 18 }),
      el('div.title', {}, title),
      mark && el('span.badge.label', {}, mark.badge),
    ),
    el('div.sub', {}, sub),
  );
  for (const v of variant.split(' ')) card.classList.add(v);
  return card;
}
